import _ from "lodash"
import { ImmutableBehaviorSubject as BehaviorSubject } from "data/lib/immutable-behavior-subject"
import Client from "data/_client"
import { navigate } from '@reach/router'
import { ApiResult } from "data/interface/api"
import folderService from "data/folder"
import folderPermissionService from "data/folder-permission"
import propertyService from "data/property"
import notificationService from "data/notification"
import { logEvent } from "data/lib/log-event"
import { local } from "data/lib/local-json"
import loadingService from "data/loading"
import { progress } from "components/notification/subscribe"

// private module logic //
const id: string = "snapshot"
const initialState: ApiResult<object[]> = { id, state: "init", value: [] }
const snapshotStream = new BehaviorSubject<ApiResult<object[]>>(initialState)
const snapshotHost = process.env.SNAPSHOT_API;
let   snapshotMap = {}
let   fileMap = {}
const snapshotEventStream = new BehaviorSubject<{ type: string; payload: any }>({
  type: '',
  payload: {}
})

// TODO see client_legacy/src/components/snapshots-restore/restore-confirmation-modal.js for handling
//      asset management warning returned by snapshot validation.  (TL;DR) split on newlines.

function get(snapshotId: number): any {
  return snapshotMap[snapshotId]
}

async function checkAssets(contentType, id?){
  const currentFolder = _.last(local('folderPath'))
  const currentProperty = local('property')
  let currentId = id ? id : contentType === 'folder' ? currentFolder : currentProperty;
  let assets = await Client.get(`migration/snapshot/${contentType}/${currentId}`)
  return !!assets.length
}

function createSnapshot(contentType: string, description: string | null = null, downloadAuto: boolean, itemId?: number, fid?: number): Promise<void> {
  const userid = _.get(local('auth.profile', {}), 'user_id')
  const folderid = fid || _.last(local('folderPath', []))
  loadingService.toggleLoading(true, 'createSnapshot')
  return folderPermissionService.getFolderUserPermissions(folderid, userid)
    .then((permissions) => {
      if ((contentType === 'folder' && permissions['owner']) || contentType === 'property') {
        const currentFolder: number = fid || _.last(local('folderPath'))
        const currentProperty:number = local('property')
        let currentId = itemId ? itemId : contentType === 'folder' ? currentFolder : currentProperty;
        let route = `snapshot?contenttype=${contentType}&ids=${currentId}&autodownload=${downloadAuto}`;
        if (description){ route += `&description=${description}` }

        const source = { [`${contentType}id`]: currentId, folderid: currentFolder }

        const component = progress('user', (event, update, unsubscribe) => {
          if (event.msg.correlationid != correlationid) return
          const progress = event.msg.progress*100|0
          if(progress == 100) {
            unsubscribe()
            //notificationService.notify({ type: 'success', message: 'The snapshot was created', dismiss: 1 })
          }
          update(progress)
        })

        let correlationid
        loadingService.toggleLoading(false, 'createSnapshot')
        return logEvent(`export-${contentType}-snapshot`, {source})
          .then(cid => {
            correlationid = cid
            notificationService.enqueue({ message: `Creating snapshot`, correlationid, component })
          })
          .then(() => Client.post(route, {userid}, {host: snapshotHost, headers: {correlationid}}))
          .catch((err: {message: string}) => {
            snapshotStream.next({ id, state: "error", error: err, value: snapshotStream.getValue().value })
            loadingService.toggleLoading(false, 'createSnapshot')
          })
      } else {
        notificationService.notify({
          type: "warn",
          message: "Only Folder Owners can create a folder snapshot",
          dismiss: 3000
        })
        loadingService.toggleLoading(false, 'createSnapshot')
      }
    })
}

function completeSnapshot(snapshotResult , contentType, downloadAuto): void {
  let snapshotId = snapshotResult.snapshotid
  if (snapshotResult.status === false){
    notificationService.notify({ type: "warn", message: snapshotResult.message || `The ${contentType} snapshot could not be created. Please try again.`, dismiss: 5000 });
    snapshotStream.next({ id, state: "error", error: snapshotResult, value: snapshotStream.getValue().value })
  }
  else {
    snapshotStream.next({ id, state: "ok", value: snapshotStream.getValue().value })
    snapshotEventStream.next({ type: 'SNAPSHOT_CREATED', payload: {}})
    if (downloadAuto == 'true') {
      console.log('download')
      downloadSnapshot(snapshotId)}
  }

}

function downloadSnapshot(snapshotId: number): void {
  let downloadSnapshotRoute = `download?snapshotid=${snapshotId}`;
  Client.get(downloadSnapshotRoute, {host: snapshotHost})
    .then((res)=>{
      const arr = _.split(res.url, '/')
      const folder = arr[3]
      const resource = _.get(_.split(arr[4], '?', 1), '[0]')
      Client.get(`snapshotshare/${folder}/${resource}`)
        .then((snapshot) => {
          const fileURL = "data:text/json;charset=utf-8," + encodeURIComponent(JSON.stringify(snapshot))
          const fileLink = document.createElement('a')
          fileLink.href = fileURL
          fileLink.download = resource
          fileLink.click()
        })
    });
}

async function shareSnapshot(snapshotId: number) {
  const { shareid } = await Client.post(`snapshotshare/${snapshotId}`, {expiry: 30})
  const shareUrl = `${process.env.CLIENT_URL}/n/folder/explorer?snapshot=$${shareid}`
  const clipboardRes = await navigator.permissions.query({name: "clipboard-write" as PermissionName})
  if (clipboardRes.state == 'granted' || clipboardRes.state == 'prompt') {
    await navigator.clipboard.writeText(shareUrl)
  }
  return shareUrl
}

const fileinfo = {}
function uploadSnapshot(file:any) {
  const folderid = _.last(local('folderPath'))
  const profile = local('auth.profile');
  const userid = profile['user_id']
  loadingService.toggleLoading(true, 'uploadSnapshot')
  const onError = () => {
    loadingService.toggleLoading(false, 'uploadSnapshot')
    notificationService.notify({
      type: "warn",
      message: "The snapshot could not be uploaded at this time.",
      dismiss: 5000
    })
  }
  Client.get(`permission/folder/${folderid}/${userid}`).then((permission) => {
    if (permission['owner']){
      let data = new FormData();
      data.append('file', file, file.name)
      Client.post('upload', data, {host: snapshotHost, headers: {'Content-Type': 'multipart/form-data'}}).then((res)=>{
        if (res.status === 'success'){
          fileinfo[res.snapshotid] = {
            name: file.name,
            ...res
          }
          validateSnapshot(res.snapshotid, file.name);
        } else {
          onError()
        }
      })
      .catch(onError)
    }
  })
}

function validateSnapshot(snapshotId, filename) {
  fileMap[snapshotId] = filename
  const folderid = _.last(local('folderPath'))
  const profile = local('auth.profile');
  const userid = profile['user_id']
  Client.get(`permission/folder/${folderid}/${userid}`).then((permission) => {
    if (permission['owner']){
      // Busy spinner
      Client.get(`validate?snapshotid=${snapshotId}&folderid=${folderid}`, {host: snapshotHost}).then((res)=>{
        snapshotEventStream.next({ type: 'SNAPSHOT_RESTORE_CONF', payload: {message: res.results.message, snapshotId, filename} })
        snapshotEventStream.next({ type: 'CLEAR', payload: null })
      });
      loadingService.toggleLoading(false, 'uploadSnapshot')
    } else {
      notificationService.notify({
        type: "warn",
        message: "Only Folder Owners can restore snapshots",
        dismiss: 3000
      })
      loadingService.toggleLoading(false, 'uploadSnapshot')
    }
  })
}

function fetchSnapshots(folderid: number|null, propertyid: number|null){
  snapshotStream.next({ id, state: 'busy', value: snapshotStream.getValue().value })

  let url = propertyid ? `snapshots?propertyid=${propertyid}` : `snapshots?folderid=${folderid}`

  Client.get(url, {host: snapshotHost}).then((res) => {
    const snapshots = res.snapshots
    _.forEach(snapshots, (snapshot) => (snapshotMap[snapshot.snapshotid] = snapshot))
    snapshotStream.next({ id, state: 'ok', value: snapshots })
    return snapshots
  })
  .catch((err: { message: string }) => {
    notificationService.notify({ type: 'danger', message: err.message, dismiss: 5000 })
    return err
  })
}

function restore(snapshotid:number, fid?:number){
  const userid = _.get(local('auth.profile', {}), 'user_id')
  const folderid = fid || _.last(local('folderPath'))
  loadingService.toggleLoading(true, 'restore')
  const component = progress('user', (event, update, unsubscribe) => {
    if (event.msg.snapshotid != snapshotid) return
    const progress = event.msg.progress*100|0
    if(progress == 100) {
      unsubscribe()
      //notificationService.notify({ type: 'success', message: 'The snapshot was successfully restored', dismiss: 5000 })
    }
    update(progress)
  })
  notificationService.enqueue({ message: `Restoring snapshot`, correlationid: `${userid}-${folderid}`, component })
  loadingService.toggleLoading(false, 'restore');
  return logEvent(`restore-snapshot`, {source: {filename: fileMap[snapshotid]}, destination: {folderid: fid}})
    .then(correlationid =>
      Client.post(`restore?snapshotid=${snapshotid}&folderid=${folderid}`, {userid}, {host: snapshotHost, headers:{correlationid}})
    )
}

function completeRestore(snapshotResult, folderId: number){
  const onError = (notif = {}) => {
    //loadingService.toggleLoading(false, 'restore')
    notificationService.notify({ type: 'danger', message: 'Unable to restore snapshot', dismiss: 5000, ...notif })
  }
  try{
    if(snapshotResult.status.toLowerCase() === 'success') {
      snapshotEventStream.next({ type: 'SNAPSHOT_RESTORE_COMPLETE', payload: {} })

      propertyService.clear()
      propertyService.fetchByFolderId(folderId)
      const objUrl = new URL(window.location.toString())
      objUrl.search = ''
      objUrl.hash = ''
      const url = objUrl.toString()
      navigate(url) // rewrite url without query string params for snapshot share
    } else {
      onError()
    }
  }
  catch(error){
     onError({ type: 'warn', message: 'Network error. Restore may be incomplete.' })
  }
}

async function restoreShared(snapshotUrl) {
  loadingService.toggleLoading(true, 'uploadSnapshot')
  try {
    let sharedSnapshot
    if (snapshotUrl.startsWith('$')){
      sharedSnapshot = await Client.get(`snapshotshare/${snapshotUrl}`)
    } else {
      const arr = _.split(snapshotUrl, '/')
      const folder = arr[3]
      const resource = _.get(_.split(arr[4], '?', 1), '[0]')
      sharedSnapshot = await Client.get(`snapshotshare/${folder}/${resource}`)
    }
    if(_.get(sharedSnapshot, 'header')) {
      const inboxRes = await Client.get('folder?code=eq.*Inbox&parentfolder=is.null')
      let inboxFolder = _.get(inboxRes, '[0]')
      if(!inboxFolder) {
        inboxFolder = await Client.post('msg/svc_create_folder', {_synchronous:true, parentfolder: null, code: '*Inbox', name: '*Inbox', systeminput: 'systemImmutable'})
        if(!inboxFolder) {
          notificationService.notify({
            type: "warn",
            message: "Unable to create *Inbox folder, please contact support",
            dismiss: 5000
          })
          loadingService.toggleLoading(false, 'uploadSnapshot')
          return
        }
        folderService.fetchFolder(_.get(inboxFolder, 'folderid'))
      }
      const userEmailPrefix = local('auth.profile').email.split('@')[0]
      const userFolderRes = await Client.get(`folder?code=eq.${userEmailPrefix}&parentfolder=eq.${_.get(inboxFolder, 'folderid')}`)
      let userFolder = _.get(userFolderRes, '[0]')
      if(!userFolder) {
        userFolder = await Client.post('msg/svc_create_folder', { _synchronous:true, code: userEmailPrefix, parentfolder: _.get(inboxFolder, 'folderid')})
        if(!userFolder) {
          notificationService.notify({
            type: "warn",
            message: "Unable to create user folder under *Inbox, please contact support",
            dismiss: 5000
          })
          loadingService.toggleLoading(false, 'uploadSnapshot')
          return
        }
        // force the translators to get the updated folder so the new name will be visible
        folderService.fetchFolder(_.get(userFolder, 'folderid'))
      }
      const data = new FormData()
      data.append('shared_snapshot', new Blob([JSON.stringify(sharedSnapshot)], {type: 'application/json'}), 'Shared')
      const uploadRes = await Client.post('upload', data, {host: snapshotHost, headers: {'Content-Type': 'multipart/form-data'}})
      if (_.get(uploadRes, 'status') === 'success'){
        const snapshotId = _.get(uploadRes, 'snapshotid')
        const folderId = _.get(userFolder, 'folderid')
        const validateRes = await Client.get(`validate?snapshotid=${snapshotId}&folderid=${folderId}`, {host: snapshotHost})
        snapshotEventStream.next({ type: 'SNAPSHOT_RESTORE_CONF', payload: {message: validateRes.results.message, snapshotId, folderId, filename: 'Shared'} })
        snapshotEventStream.next({ type: 'CLEAR', payload: null })
        propertyService.fetchByFolderId(_.get(userFolder, 'folderid'))
      } else {
        notificationService.notify({
          type: "warn",
          message: "Unable to upload snapshot please contact support",
          dismiss: 5000
        })
        loadingService.toggleLoading(false, 'uploadSnapshot')
        return
      }
    } else {
      notificationService.notify({
        type: "warn",
        message: "Unable to download shared snapshot, please contact support",
        dismiss: 5000
      })
    }
    loadingService.toggleLoading(false, 'uploadSnapshot')
  } catch (e) {
    const message =
      _.get(e,'message') == 'snapshot not found' ?
      "Shared snapshot has expired" :
      "Restoring of shared snapshot failed, please contact support";

    notificationService.notify({
      type: "warn",
      message,
      dismiss: 5000
    })
    loadingService.toggleLoading(false, 'uploadSnapshot')
  }
}

// public module interface //

const snapshotService = {
  stream: () => snapshotStream.asObservable(),
  eventStream: () => snapshotEventStream.asObservable(),
  createSnapshot: (contentType: string, description: string | null = null, downloadAuto: boolean, itemId?: number, fid?: number) => createSnapshot(contentType, description, downloadAuto, itemId, fid),
  completeSnapshot: (snapshotId: number, contentType, downloadAuto) => completeSnapshot(snapshotId, contentType, downloadAuto),
  completeRestore: (snapshotResult, folderId: number) => completeRestore(snapshotResult, folderId),
  checkAssets,
  downloadSnapshot,
  fetchSnapshots,
  shareSnapshot,
  uploadSnapshot,
  get,
  restore,
  restoreShared
}

export default snapshotService
